// src/components/Admin/SubscriptionDebugger.js
import React, { useState, useEffect } from 'react';
import { useUserContext } from '../../contexts/UserContext';
import './Admin.css';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://http://https://southamerica-east1-drecenna.cloudfunctions.net/api';

const SubscriptionDebugger = () => {
  const { user, subscription, loading } = useUserContext();
  const [apiData, setApiData] = useState(null);
  const [firestoreData, setFirestoreData] = useState(null);
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);
  const [fixAttempted, setFixAttempted] = useState(false);
  const [fixResult, setFixResult] = useState(null);

  useEffect(() => {
    if (user && !loading) {
      checkSubscriptionDirectly();
    }
  }, [user, loading]);

  const checkSubscriptionDirectly = async () => {
    if (!user) return;
    
    setChecking(true);
    setError('');
    
    try {
      // Verificar diretamente pelo endpoint de status
      const response = await axios.get(`${API_URL}/subscription-status?uid=${user.uid}`);
      setApiData(response.data);
      
      // Tentar obter dados brutos do Firestore (simulação, seria melhor com função no servidor)
      // Este código é apenas para debug, em produção você teria um endpoint dedicado
      const firestoreResponse = await axios.post(`${API_URL}/debug-get-user-data`, { 
        uid: user.uid,
        debugKey: 'dev_access_only' // Uma chave simples para restringir acesso 
      }).catch(() => ({ data: null }));
      
      if (firestoreResponse.data) {
        setFirestoreData(firestoreResponse.data);
      }
    } catch (error) {
      console.error('Erro ao verificar status da assinatura:', error);
      setError(error.response?.data?.error || 'Erro ao verificar assinatura');
    } finally {
      setChecking(false);
    }
  };

  const attemptFixSubscription = async () => {
    if (!user) return;
    
    setChecking(true);
    setError('');
    setFixAttempted(true);
    
    try {
      // Chamar endpoint de correção de assinatura (precisará ser implementado no backend)
      const response = await axios.post(`${API_URL}/admin/fix-subscription`, { 
        uid: user.uid,
        adminKey: 'dev_access_only' // Substitua por autenticação adequada
      });
      
      setFixResult(response.data);
      // Atualizar os dados após a correção
      checkSubscriptionDirectly();
    } catch (error) {
      console.error('Erro ao tentar corrigir assinatura:', error);
      setError(error.response?.data?.error || 'Erro ao corrigir assinatura');
      setFixResult({ success: false, message: error.message });
    } finally {
      setChecking(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch (e) {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="admin-container">
        <div className="admin-card loading-card">
          <div className="loading-spinner"></div>
          <p>Carregando informações do usuário...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="admin-container">
        <div className="admin-card">
          <h2>Depurador de Assinatura</h2>
          <div className="error-message">Usuário não autenticado</div>
          <p>Faça login para usar esta ferramenta</p>
          <button onClick={() => window.location.href = '/login'}>Ir para Login</button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-card">
        <h2>Depurador de Assinatura</h2>
        <p>Esta ferramenta ajuda a identificar problemas com sua assinatura</p>
        
        {error && <div className="error-message">{error}</div>}
        
        <div className="results-details">
          <h3>Informações do Usuário</h3>
          <p><strong>UID:</strong> {user.uid}</p>
          <p><strong>Email:</strong> {user.email}</p>
          <p><strong>Nome:</strong> {user.displayName || 'Não definido'}</p>
        </div>
        
        {subscription && (
          <div className="results-details">
            <h3>Dados de Assinatura (Contexto)</h3>
            <p><strong>Status:</strong> {subscription.status}</p>
            <p><strong>Ativa?</strong> {subscription.active ? 'Sim' : 'Não'}</p>
            <p><strong>Tipo de Plano:</strong> {subscription.planType || 'Não definido'}</p>
            <p><strong>Expiração:</strong> {formatDate(subscription.currentPeriodEnd)}</p>
            <p><strong>Expirada?</strong> {subscription.isExpired ? 'Sim' : 'Não'}</p>
          </div>
        )}
        
        {apiData && (
          <div className="results-details">
            <h3>Dados da API</h3>
            <p><strong>Status:</strong> {apiData.status}</p>
            <p><strong>Ativa?</strong> {apiData.active ? 'Sim' : 'Não'}</p>
            <p><strong>Tipo de Plano:</strong> {apiData.planType || 'Não definido'}</p>
            <p><strong>Expiração:</strong> {formatDate(apiData.currentPeriodEnd)}</p>
            <p><strong>Expirada?</strong> {apiData.isExpired ? 'Sim' : 'Não'}</p>
            <p><strong>Dias Restantes:</strong> {apiData.daysRemaining}</p>
          </div>
        )}
        
        {firestoreData && (
          <div className="results-details">
            <h3>Dados Brutos do Firestore</h3>
            <div className="collapsible-data">
              <details>
                <summary>Ver dados completos</summary>
                <pre>{JSON.stringify(firestoreData, null, 2)}</pre>
              </details>
            </div>
          </div>
        )}
        
        {fixAttempted && fixResult && (
          <div className={`${fixResult.success ? 'success-message' : 'warning-message'}`}>
            <p><strong>Resultado da correção:</strong> {fixResult.message}</p>
          </div>
        )}
        
        <div className="option-buttons">
          <button 
            onClick={checkSubscriptionDirectly} 
            disabled={checking}
            className="secondary-button"
          >
            {checking ? "Verificando..." : "Verificar Novamente"}
          </button>
          
          <button 
            onClick={attemptFixSubscription} 
            disabled={checking}
            className="action-button"
          >
            {checking ? "Corrigindo..." : "Tentar Corrigir Assinatura"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionDebugger;